// ElectionModeMapView.js
import React, { useEffect, useRef, useState } from "react";
import {
  GoogleMap,
  Polygon,
  InfoWindow,
} from "@react-google-maps/api";

import { Button } from "antd";

import AddElectionModeShape from "./AddElectionModeShape";
import ElectionModeDashboard from "./ElectionModeDashboard";
import { useQuery } from "react-query";
import useAxiosInstance from "../../lib/useAxiosInstance";

const ElectionModeMapView = ({
  isLoaded,
  open,
  setIsFullScreen,
  id,
  center,
  polygonPaths = [],
  mapRef,
  electionMode,
}) => {
  const internalRef = useRef(null);
  const axiosInstance = useAxiosInstance();
  const mapRefToUse = mapRef || internalRef;

  const [selectedPolygon, setSelectedPolygon] = useState(null);

  const [showStateShape, setShowStateShape] = useState(false);
  const [selectedLocationName, setSelectedLocationName] = useState(null);
  const [mapAction, setMapAction] = useState(null);
  // const [dashboardMapState, setDashboardMapState] = useState({
  //   district: "",
  //   constituency: "",
  //   selectedConstituencies: [],
  //   polygonColors: [],
  //   constituencySummaries: {},
  // });

  const [dashboardMapState, setDashboardMapState] = useState({
  region: "",
  district: "",
  constituency: "",
  selectedConstituencies: [],
});

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [open]);
  
  const { data: polygonData } = useQuery(
    [
      "surveyColors",
      id,
      dashboardMapState.region,
      dashboardMapState.district,
      dashboardMapState.constituency,
    ],
    async () => {
      const res = await axiosInstance.get(
        `/api/electionMode/polygonColor/${id}`,
        {
          params: {
            region: dashboardMapState.region || undefined,
            district: dashboardMapState.district || undefined,
            constituency: dashboardMapState.constituency || undefined,
          },
        }
      );

      return res.data?.data || [];
    },
    {
      enabled: !!id,
      refetchOnWindowFocus: false,
    }
  );


  const fitToPolygons = (mapInstance, polygons, fallbackCenter) => {
    if (
      !window.google ||
      !mapInstance ||
      !polygons ||
      polygons.length === 0
    ) {
      if (fallbackCenter) {
        mapInstance?.setCenter(fallbackCenter);
        mapInstance?.setZoom(10);
      }

      return;
    }

    const bounds = new window.google.maps.LatLngBounds();

    polygons.forEach((polygon) => {
      polygon.coordinate?.forEach((point) => {
        // bounds.extend({
        //   lat: Number(point.lat),
        //   lng: Number(point.lng),
        // });
        const lat = Number(point?.lat);
        const lng = Number(point?.lng);
        if (!isNaN(lat) && !isNaN(lng)) bounds.extend({ lat, lng });
      });
    });

    if (!bounds.isEmpty()) {
      mapInstance.fitBounds(bounds);
      mapInstance.setCenter(bounds.getCenter());
    } else if (fallbackCenter) {
      mapInstance.setCenter(fallbackCenter);
      mapInstance.setZoom(10);
    }
  };

  useEffect(() => {
    if (!isLoaded || !mapRefToUse.current) return;

    const timer = setTimeout(() => {
      fitToPolygons(
        mapRefToUse.current,
        polygonPaths,
        center
      );
    }, 100);

    return () => clearTimeout(timer);
  }, [polygonPaths, isLoaded, center]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedLocationName || !polygonPaths?.length) {
      if (!selectedLocationName) setSelectedPolygon(null);
      return;
    }

    const match = polygonPaths.find(
      (polygon) =>
        polygon.name?.toString().trim().toLowerCase() ===
        selectedLocationName.toString().trim().toLowerCase()
    );

    if (match) handlePolygonClick(match);
  }, [selectedLocationName, polygonPaths]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const areaSelected = dashboardMapState.district || dashboardMapState.region;
    if (
      !areaSelected ||
      dashboardMapState.constituency ||
      !dashboardMapState.selectedConstituencies?.length ||
      !window.google ||
      !mapRefToUse.current
    ) {
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    polygonPaths
      .filter((polygon) => isPolygonHighlighted(polygon.name))
      .forEach((polygon) => {
        polygon.coordinate?.forEach((point) => bounds.extend(point));
      });

    if (!bounds.isEmpty()) {
      mapRefToUse.current.fitBounds(bounds);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dashboardMapState.region,
    dashboardMapState.district,
    dashboardMapState.constituency,
    dashboardMapState.selectedConstituencies,
    polygonPaths,
  ]);

  const handlePolygonLoad = (polygonInstance) => {
    polygonInstance.setMap(mapRefToUse.current);
    };

  const handlePolygonClick = (polygon) => {
    setSelectedPolygon(polygon);
    setSelectedLocationName(polygon.name);

    if (!window.google || !mapRefToUse.current) return;

    const bounds = new window.google.maps.LatLngBounds();

    polygon.coordinate?.forEach((p) => {
      bounds.extend({
        lat: Number(p.lat),
        lng: Number(p.lng),
      });
    });

    if (!bounds.isEmpty()) {
      mapRefToUse.current.fitBounds(bounds);
    }
  };

  const handleBackClick = () => {
    setSelectedPolygon(null);
    setSelectedLocationName(null);

    if (!window.google || !mapRefToUse.current) return;

    const bounds = new window.google.maps.LatLngBounds();

    polygonPaths.forEach((poly) => {
      poly.coordinate?.forEach((point) => {
        bounds.extend(point);
      });
    });

    mapRefToUse.current.fitBounds(bounds);
  };

  const normalize = (value) => value?.toString().trim().toLowerCase();

  // const getPolygonColor = (name) => {
  //   const match = dashboardMapState.polygonColors.find(
  //     (item) => normalize(item.constituency) === normalize(name)
  //   );
  //   return match?.color || "#3bbee8";
  // };

  const getPolygonColor = (name) => {
    const match = polygonData?.find(
      (item) => normalize(item.constituency) === normalize(name)
    );

    return match?.color || "#3bbee8";
  };

  // const getPolygonMeta = (name) =>
  //   dashboardMapState.polygonColors.find(
  //     (item) => normalize(item.constituency) === normalize(name)
  //   ) || dashboardMapState.constituencySummaries?.[name] || {};

  const getPolygonMeta = (name) =>
  polygonData?.find(
    (item) => normalize(item.constituency) === normalize(name)
  ) || {};

  const formatPercent = (value) => {
    const number = Number(value) || 0;
    const percent = Math.abs(number) <= 1 ? number * 100 : number;
    return `${percent.toFixed(1)}%`;
  };

  const isPolygonHighlighted = (name) =>
    dashboardMapState.selectedConstituencies.some(
      (item) => normalize(item) === normalize(name)
    );

  const getAreaInfoPosition = () => {
    const areaSelected = dashboardMapState.district || dashboardMapState.region;
    if (
      !areaSelected ||
      !dashboardMapState.selectedConstituencies?.length ||
      !window.google
    ) {
      return null;
    }

    const bounds = new window.google.maps.LatLngBounds();
    polygonPaths
      .filter((polygon) => isPolygonHighlighted(polygon.name))
      .forEach((polygon) => {
        polygon.coordinate?.forEach((point) => bounds.extend(point));
      });

    return bounds.isEmpty() ? null : bounds.getCenter();
  };
  
    const renderPolygons = () => {
        if (!polygonPaths || !Array.isArray(polygonPaths)) return null;
        if (!mapRefToUse.current || !window.google) return null;
    
        return polygonPaths.map((polygon, index) => {
          const bounds = new window.google.maps.LatLngBounds();
          // polygon.coordinate?.forEach((p) =>
          //   bounds.extend({ lat: Number(p.lat), lng: Number(p.lng) })
          // );
          polygon?.coordinate?.forEach((point) => bounds.extend(point));
    
          const isSelected = selectedPolygon?.name === polygon.name;
          const isHighlighted = isPolygonHighlighted(polygon.name);
          const meta = getPolygonMeta(polygon.name);
          const selectedParty = meta.selectedParty || {};
    
        //   const legendColor = getLegendColor(polygon.name)
        
          return (
            <React.Fragment key={index}>
              <Polygon
                paths={polygon?.coordinate}
                onClick={() => handlePolygonClick(polygon)}
                onLoad={handlePolygonLoad}
                options={{
                  strokeColor: "#0D47A1",
                  strokeOpacity: 0.9,
                  strokeWeight: isSelected || isHighlighted ? 4 : 2,
                  fillColor: getPolygonColor(polygon.name),
                  fillOpacity: isSelected || isHighlighted ? 0.82 : 0.58,
                  zIndex: isSelected || isHighlighted ? 1000 : 999,
                }}
              />
              {isSelected && (
              <InfoWindow
                // position={(() => {
                //   const bounds = new window.google.maps.LatLngBounds();
                //   selectedPolygon.coordinate?.forEach((p) =>
                //     bounds.extend({ lat: Number(p.lat), lng: Number(p.lng) })
                //   );
                //   return bounds.getCenter();
                // })()}
                position={bounds.getCenter()}
                onCloseClick={handleBackClick}
                options={{ maxWidth: 340 }}
              >
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xl min-w-[280px]">
                    <div className="flex items-center gap-2 mb-3">
                      <span
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: meta.color || "#3bbee8" }}
                      />
                      <div className="text-lg font-bold text-[#123865]">
                      {selectedPolygon.name}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-slate-700">
                      <div className="rounded-lg bg-slate-50 p-2">
                        <div className="text-slate-500">Winning Party</div>
                        <strong>{meta.winningParty || "-"}</strong>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-2">
                        <div className="text-slate-500">Winner</div>
                        <strong>{meta.winnerCandidate || "-"}</strong>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-2">
                        <div className="text-slate-500">Vote Share</div>
                        <strong>{formatPercent(meta.winnerVoteSharePercent || meta.winnerVoteShare)}</strong>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-2">
                        <div className="text-slate-500">Margin</div>
                        <strong>{formatPercent(meta.winnerMargin)}</strong>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-2">
                        <div className="text-slate-500">Runner-up Party</div>
                        <strong>{meta.runnerUpParty || "-"}</strong>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-2">
                        <div className="text-slate-500">Runner-up</div>
                        <strong>{meta.runnerUpCandidate || "-"}</strong>
                      </div>
                    </div>
                    <div
                      className="mt-3 rounded-lg p-2 text-xs font-semibold"
                      style={{
                        background: `${selectedParty.color || "#0b65c2"}18`,
                        color: selectedParty.color || "#0b65c2",
                        border: `1px solid ${selectedParty.color || "#0b65c2"}55`,
                      }}
                    >
                      Selected Party: {selectedParty.party || "-"} · {selectedParty.category || "No category"}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 justify-end">
                      <Button
                        size="small"
                        onClick={() =>
                          setMapAction({
                            id: Date.now(),
                            type: "note",
                            scope: "constituency",
                            constituency: selectedPolygon.name,
                          })
                        }
                      >
                        Add Note
                      </Button>
                      <Button
                        size="small"
                        onClick={() =>
                          setMapAction({
                            id: Date.now(),
                            type: "report",
                            scope: "constituency",
                            constituency: selectedPolygon.name,
                          })
                        }
                      >
                        Add Reports
                      </Button>
                      <Button onClick={handleBackClick} size="small">
                        Back
                      </Button>
                    </div>
                  </div>
              </InfoWindow>
            )}
            </React.Fragment>
          );
        });
      };

  const onLoad = (map) => {
    mapRefToUse.current = map;

    if (polygonPaths?.length) {
      fitToPolygons(map, polygonPaths, center);
    } else {
      map.setCenter(center);
    }
  };

  if (!isLoaded) return <div>Loading map...</div>;

  return (
    <div
      className={`${
        open
          ? "fixed inset-0 z-[9] h-[95%] w-[98%] mx-5 my-5"
          : ""
      }`}
    >
      {open && (
        <ElectionModeDashboard
          electionModeId={id}
          selectedConstituencyFromMap={selectedLocationName}
          mapAction={mapAction}
          onLocationSelect={setSelectedLocationName}
          onDashboardMapChange={setDashboardMapState}
          electionModeType={electionMode?.electionModeType}
          urbanRural={electionMode?.client?.urbanRural}
        />
      )}

      <GoogleMap
        mapContainerClassName={`w-full ${
          open ? "h-[95vh]" : "h-[500px]"
        }`}
        defaultCenter={center}
        defaultZoom={10}
        onLoad={onLoad}
        options={{
          fullscreenControl: false,
        }}
      >
        {renderPolygons()}
        {open &&
          (dashboardMapState.district || dashboardMapState.region) &&
          !dashboardMapState.constituency &&
          getAreaInfoPosition() && (
            <InfoWindow position={getAreaInfoPosition()} options={{ maxWidth: 300 }}>
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xl">
                <div className="text-base font-bold text-[#123865]">
                  {dashboardMapState.district || dashboardMapState.region}
                </div>
                <div className="text-xs text-slate-600">
                  {dashboardMapState.selectedConstituencies.length} constituencies selected
                </div>
                {dashboardMapState.district && (
                <div className="mt-3 flex gap-2 justify-end">
                  <Button
                    size="small"
                    onClick={() =>
                      setMapAction({
                        id: Date.now(),
                        type: "note",
                        scope: "district",
                        district: dashboardMapState.district,
                      })
                    }
                  >
                    Add Note
                  </Button>
                  <Button
                    size="small"
                    onClick={() =>
                      setMapAction({
                        id: Date.now(),
                        type: "report",
                        scope: "district",
                        district: dashboardMapState.district,
                      })
                    }
                  >
                    Add Reports
                  </Button>
                </div>
                )}
              </div>
            </InfoWindow>
          )}
      </GoogleMap>

      {open && (
        <Button
          onClick={() => setIsFullScreen(false)}
          type="primary"
          danger
          className="absolute top-4 right-4 z-50 bg-white text-black"
        >
          Close
        </Button>
      )}

      {!electionMode?.isShapeExist && (
        <button
          onClick={() => setShowStateShape(true)}
          style={{
            position: "absolute",
            bottom: "50%",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#1677ff",
            color: "white",
            borderRadius: "50%",
            width: "65px",
            height: "65px",
            border: "none",
            fontSize: "30px",
            cursor: "pointer",
            zIndex: 50,
            boxShadow: "0 4px 14px rgba(0,0,0,0.3)",
          }}
        >
          +
        </button>
      )}

      <AddElectionModeShape
        isOpen={showStateShape}
        id={id}
        setShowStateShape={setShowStateShape}
      />
    </div>
  );
};

export default ElectionModeMapView;
